import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timeConverter } from '@/utils/time';
import supabase from '@/configs/supabse';
import useAuthStore from '@/store/authStore';
// import { validStoreLocation } from './generalHooks';
// import { generateRandomId } from '@/utils/strings';
import httpsCall from '@/services/httpsCall';
import logger from '@/utils/logger';

interface CreateEmployeeInput {
  name: string;
  email: string;
  phone: string;
  active: boolean;
  role: 'manager' | 'employee';
  store_location_id: string;
}

interface UpdateEmployeeInput {
  id: number;
  // userId: string;
  name: string;
  email: string;
  phone: string;
  active: boolean;
  role: string;
  store_location_id: string;
}

interface CreateEmployeeByManagerInput {
  name: string;
  email: string;
  phone: string;
  // status: 'active' | 'inactive';
  role: string;
  store: string;
}

interface UpdateEmployeeByManagerInput {
  id: string;
  name: string;
  email: string;
  phone: string;
  // active: 'active' | 'inactive';
  role: any;
  store: any;
  active: boolean;
}

export interface EmployeesResponse {
  content: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    paged: boolean;
  };
  uniqueId: any;
  name: string;
  email: string;
  phone: string;
  store: string;
  role: any;
  active: string;
  id: string;
  status: boolean;
}

export interface OwnerStoreResponse {
  content: any[];
  pages?: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    paged: boolean;
  };
}

// * ====================== Fetch All Employees ====================== *
const fetchAllEmployees = async ({
  page = 0,
  size = 10,
  keyword = '',
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<EmployeesResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/employee-list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as EmployeesResponse;
  } catch (error: any) {
    logger.error('Error fetching :', error);
    throw error;
  }
};
export const useFetchAllEmployees = (
  page: number = 0,
  size: number = 10,
  keyword: string = '',
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<EmployeesResponse, Error>({
    queryKey: ['employees', page, size, keyword, sortBy, sortDir],
    queryFn: () => fetchAllEmployees({ page, size, keyword, sortBy, sortDir }),
    placeholderData: (previousData) => previousData,
  });
};

// * ====================== Fetch Single Employee ====================== *
const fetchSingleEmployee = async (employeeId: string): Promise<EmployeesResponse | null> => {
  const response = await httpsCall.get(`/owner/employee/${employeeId}`);
  return response?.data?.data;
};

export const useFetchSingleEmployee = (employeeId?: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => fetchSingleEmployee(employeeId!),
    enabled: !!employeeId && enabled,
    retry: false,
    staleTime: timeConverter(10, 'minute'),
  });
};

// * ====================== Create Employee ====================== *
export const createEmployee = async (employeeData: CreateEmployeeInput) => {
  const { name, email, phone, role, active, store_location_id } = employeeData;

  const response = await httpsCall.post('/owner/add-employee', {
    name,
    email,
    phone,
    role: { id: role },
    active,
    store: { id: store_location_id },
  });
  return response.data;
};

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};

// * ====================== Fetch Role List ====================== *
const fetchRoleList = async () => {
  const response = await httpsCall.get(`/owner/role-list`);
  return response.data?.data;
};

export const useFetchRoleData = () => {
  return useQuery({
    queryKey: ['role-list'],
    queryFn: fetchRoleList,
  });
};

// * ====================== Delete Employee ====================== *
export const deleteEmployee = async (id: string) => {
  const response = await httpsCall.delete(`/owner/delete-employee/${id}`);
  return response.data;
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'], exact: false });
    },
  });
};

// * ====================== Update Employee ====================== *
export const updateEmployee = async (UpdateEmployeeInput: UpdateEmployeeInput) => {
  const { id, name, email, phone, role, active, store_location_id } = UpdateEmployeeInput;
  const response = await httpsCall.put(`/owner/update-employee/${id}`, {
    name,
    email,
    phone,
    role: { id: role },
    active,
    store: { id: store_location_id },
  });
  return response?.data;
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['employee'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['owner-store-locations'], exact: false });
      // queryClient.invalidateQueries({ queryKey: ['employee-store-locations'], exact: false });
    },
  });
};

// * ====================== Fetch All Store Locations of the Store Owner ====================== *
const fetchOwnerStoreLocations = async () => {
  const response = await httpsCall.get(`/owner/store-list?page=0&size=10&Keyword=`);
  return response.data?.data;
};

export const useFetchOwnerStoreLocations = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['owner-store-locations'],
    queryFn: fetchOwnerStoreLocations,
    staleTime: timeConverter(20, 'minute'),
    enabled: options?.enabled ?? true,
  });
};

// * ====================== Fetch All Store Locations of the Store Owner V2 ====================== *
export const fetchAllOwnersStores = async ({
  page = 0,
  size = 10,
  keyword = '',
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<OwnerStoreResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/store-list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as OwnerStoreResponse;
  } catch (error: any) {
    logger.error('Error fetching owners:', error);
    throw error;
  }
};

export const useFetchOwnerStoreLocation = (
  page: number = 0,
  size: number = 10,
  keyword: string = '',
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<OwnerStoreResponse, Error>({
    queryKey: ['owner-store-locations', page, size, keyword, sortBy, sortDir],
    queryFn: () => fetchAllOwnersStores({ page, size, keyword, sortBy, sortDir }),
    placeholderData: (previousData) => previousData,
  });
};

// * ====================== Fetch All Store Locations of the Store Employee/Manager ====================== *
const fetchStoreLocationsForEmployee = async () => {
  // const userId = useAuthStore.getState().userProfile?.id;

  // const { data: employeeData, error: employeeError } = await supabase
  //   .from('store_employees')
  //   .select(
  //     `
  //     id, user_id, store_location_id,
  //     user:users (
  //       id, name, email, phone, role, status
  //     ),
  //     store_location:store_locations (
  //       id, name, status
  //     )
  //    `
  //   )
  //   .eq('user.id', userId)
  //   .not('user', 'is', null)
  //   .maybeSingle();

  // if (employeeError) throw employeeError;

  // const storeLocation = [
  //   {
  //     id: (employeeData as any).store_location?.id,
  //     name: (employeeData as any).store_location.name,
  //     status: (employeeData as any).store_location.status,
  //   },
  // ];

  // const result = storeLocation;
  return [];
};

export const useFetchStoreLocationsForEmployee = () => {
  return useQuery({
    queryKey: ['employee-store-locations'],
    queryFn: fetchStoreLocationsForEmployee,
  });
};

// * ====================== Fetch Employees who shares the same location ====================== *
const fetchEmployeesInSameLocations = async () => {
  const userId = useAuthStore.getState().userProfile?.id;
  if (!userId) throw new Error('User not found');

  // Get Employee
  const { data: employeeData, error: employeeError } = await supabase
    .from('store_employees')
    .select('id, store_location_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (employeeError) throw employeeError;

  const store_location_id = employeeData?.store_location_id;

  // Fetch the employees
  const { data, error } = await supabase
    .from('store_employees')
    .select(
      `
        id, user_id, store_location_id,
        user:users (
          id, name, email, phone, role, status, unique_id
        ),
        store_location:store_locations (
          id, name
        )
    `
    )
    .neq('user_id', userId) // Exclude current user
    .eq('store_location_id', store_location_id)
    .not('user', 'is', null)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const result = data;
  return result;
};

export const useEmployeesInSameLocations = () => {
  return useQuery({
    queryKey: ['employees-in-same-locations'],
    queryFn: fetchEmployeesInSameLocations,
  });
};

// ? ======================================
// ? By Manager
// ? ======================================

// * ====================== Create Employee ====================== *
// export const createEmployeeByManager = async (employeeData: CreateEmployeeByManagerInput) => {
//   const userProfile = useAuthStore.getState().userProfile;
//   const { name, email, phone, role, status } = employeeData;
//   const password = `Password@${new Date().getFullYear()}`;
//   const unique_id = generateRandomId();

//   const user_id = userProfile?.id;
//   const { data: userData, error: fetchUserDataError } = await supabase
//     .from('users')
//     .select(
//       `
//       *,
//       store_employee:store_employees (
//         *
//       )
//       `
//     )
//     .eq('id', user_id)
//     .maybeSingle();

//   if (fetchUserDataError) throw fetchUserDataError;
//   if (!userData) throw new Error('User not found');
//   const store_location_id = userData.store_employee?.[0].store_location_id;

//   // ? ----------- Prevent Inactive User Actions -----------
//   const isUserActive = await isCurrentUserActive();
//   const clearAuth = useAuthStore.getState().clearAuth;

//   if (!isUserActive) {
//     clearAuth();
//     useAuthStore.persist.clearStorage();
//     throw new Error('Your account has been suspended by Super Admin');
//   }

//   // ? ---------- Store Location Check -----------
//   const isValid = await validStoreLocation(store_location_id);
//   if (!isValid) {
//     throw new Error('Store location is not valid');
//   }

//   // Check if user with same email or phone number already exists
//   const { data: existingUsers, error: fetchError } = await supabase
//     .from('users')
//     .select('id')
//     .or(`email.eq.${email},phone.eq.${phone}`);

//   if (fetchError) {
//     throw fetchError;
//   }

//   if (existingUsers && existingUsers.length > 0) {
//     throw new Error('Employee with the same email or phone number already exists');
//   }

//   // Create user in Supabase Auth
//   const { data: authData, error: authError } = await supabase.auth.admin.createUser({
//     email,
//     phone,
//     password,
//     email_confirm: true,
//     user_metadata: { display_name: name },
//   });

//   if (authError || !authData?.user) {
//     throw authError || new Error('User creation failed');
//   }

//   const authUserId = authData.user.id;

//   // Insert into users table
//   const { error: dbError, data: insertedUserData } = await supabase
//     .from('users')
//     .insert({
//       auth_user_id: authUserId,
//       unique_id,
//       name,
//       email,
//       phone,
//       role,
//       status,
//       created_by_user_id: userProfile?.id,
//       updated_by_user_id: userProfile?.id,
//     })
//     .select();

//   if (dbError || !insertedUserData?.length) {
//     await supabase.auth.admin.deleteUser(authUserId); // rollback
//     throw dbError || new Error('Failed to insert user record');
//   }

//   const insertedUserId = insertedUserData[0].id;

//   // Insert into store_employees table
//   const { error: storeOwnerError } = await supabase.from('store_employees').insert({
//     store_location_id,
//     user_id: insertedUserId,
//   });

//   if (storeOwnerError) {
//     await supabase.auth.admin.deleteUser(authUserId); // rollback again
//     throw storeOwnerError;
//   }

//   return { success: true, userId: authUserId };
// };

// export const useCreateEmployeeByManager = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: createEmployeeByManager,
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['employees-in-same-locations'], exact: false });
//     },
//   });
// };

// * ====================== Create Employee ====================== *
export const createEmployeeByManager = async (employeeData: CreateEmployeeByManagerInput) => {
  // const userProfile = useAuthStore.getState().userProfile;
  const { name, email, phone, role, store } = employeeData;
  const response = await httpsCall.post('/owner/add-employee', {
    name,
    email,
    phone,
    role: { id: role },
    store: { id: store },
  });
  return response.data;
};

export const useCreateEmployeeByManager = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmployeeByManager,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'], exact: false });
    },
  });
};

// * ====================== Update Employee ====================== *
// export const updateEmployeeByManager = async ({
// userId,
// name,
// email,
// phone,
// status,
// }: UpdateEmployeeByManagerInput) => {
//   const userProfile = useAuthStore.getState().userProfile;
//   const user_id = userProfile?.id;
//   const { data: userData, error: fetchUserDataError } = await supabase
//     .from('users')
//     .select(
//       `
//       *,
//       store_employee:store_employees (
//         *
//       )
//       `
//     )
//     .eq('id', user_id)
//     .maybeSingle();

//   if (fetchUserDataError) throw fetchUserDataError;
//   if (!userData) throw new Error('User not found');
//   const store_location_id = userData.store_employee?.[0].store_location_id;

//   // ? ----------- Prevent Inactive User Actions -----------
//   const isUserActive = await isCurrentUserActive();
//   const clearAuth = useAuthStore.getState().clearAuth;

//   if (!isUserActive) {
//     clearAuth();
//     useAuthStore.persist.clearStorage();
//     throw new Error('Your account has been suspended by Super Admin');
//   }

//   // ? ---------- Store Location Check -----------
//   const isValid = await validStoreLocation(store_location_id);
//   if (!isValid) {
//     throw new Error('Store location is not valid');
//   }

//   // Optional: check for duplicates if email/phone changed (excluding self)
//   const { data: existingUsers, error: checkError } = await supabase
//     .from('users')
//     .select('id')
//     .or(`email.eq.${email},phone.eq.${phone}`)
//     .neq('id', userId);

//   if (checkError) throw checkError;
//   if (existingUsers?.length) {
//     throw new Error('Employee with this email or phone number already exists');
//   }

//   const { data: employee, error: fetchEmployeeError } = await supabase
//     .from('users')
//     .select('auth_user_id')
//     .in('role', ['employee'])
//     .eq('id', userId)
//     .maybeSingle();

//   if (fetchEmployeeError) throw fetchEmployeeError;

//   const auth_user_id = employee?.auth_user_id;

//   // Update Supabase Auth email & metadata
//   const { error: authError } = await supabase.auth.admin.updateUserById(auth_user_id, {
//     email,
//     phone,
//     user_metadata: {
//       display_name: name,
//     },
//   });
//   if (authError) throw authError;

//   // Update in users table
//   const { error: dbError } = await supabase
//     .from('users')
//     .update({
//       name,
//       email,
//       phone,
//       status,
//       updated_by_user_id: userProfile?.id,
//       updated_at: new Date().toISOString(),
//     })
//     .eq('id', userId);

//   if (dbError) throw dbError;

//   return { success: true };
// };

// export const useUpdateEmployeeByManager = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: updateEmployeeByManager,
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['employees-in-same-locations'], exact: false });
//       queryClient.invalidateQueries({ queryKey: ['employee'], exact: false });
//     },
//   });
// };

// * ====================== Update Employee new ====================== *
export const updateEmployeeByManager = async ({
  id,
  name,
  email,
  phone,
  active,
  role,
  store,
}: UpdateEmployeeByManagerInput) => {
  const response = await httpsCall.put(`/owner/update-employee/${id}`, {
    // userId,
    id,
    name,
    email,
    phone,
    active,
    role: { id: role },
    store: { id: store },
  });
  return response?.data;
};

export const useUpdateEmployeeByManager = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEmployeeByManager,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['employee'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['owner-store-locations'], exact: false });
    },
  });
};
